import React, { useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { Radio, RadioGroup } from 'tea-component';
import './index.scss';
import { TabNavigator } from '@op/components/Navigator';

import { scrollToAnchorPoint } from '@op/utils';
import TagAnalysisChart from '../TagAnalysisChart';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { isSpecialEdition } from '../utils';

interface MetricOption {
  text: string;
  value: string;
};

interface AttributeOption {
  text: string;
  value: string;
};

export interface TagOption {
  label_en: string;
  label_zh: string;
  children?: Array<{ label_en: string; label_zh: string }>;
};

interface FilterItem {
  t1: string;
  [key: string]: any;
};

interface TagAnalysisCardProps {
  filter: any;
  gameInfo: any;
  scrollBody?: any;
  navigators?: any[];
  defaultNavigator: string;
  setSelectNavigator: (value: any) => void;
  selectNavigator: any;
  tagOptions: TagOption[];
};

export const METRIC_OPTIONS: MetricOption[] = [
  { text: 'Views', value: 'views' },
  { text: 'Videos', value: 'videos' },
  { text: 'Likes', value: 'likes' },
  { text: 'Comments', value: 'comments' },
  { text: 'Creators', value: 'creators' },
];

const AttributeOptions: AttributeOption[] = [
  { text: 'Total', value: 'total' },
  { text: 'Date', value: 'time' },
  { text: 'Platform', value: 'platform' },
  { text: 'Language', value: 'language' },
];

export default function TagAnalysisCard(props: TagAnalysisCardProps) {
  const {
    filter: propsFilter,
    gameInfo,
    scrollBody,
    navigators = [],
    defaultNavigator,
    setSelectNavigator,
    selectNavigator,
    tagOptions
  } = props;
  const { formatMessage, isEn } = useLanguage();
  const [metric, setMetric] = useState('videos');
  const [attributeBy, setAttributeBy] = useState('total');
  const filter = useMemo(() => ({
    ...gameInfo,
    ...propsFilter,
  }), [gameInfo, propsFilter]);

  const tagsList = useMemo(() => {
    const { video_tags: videoTags } = filter || {};
    // 如果 videoTags 为空数组，则显示所有 tagOptions
    if (!videoTags || videoTags.length === 0) {
      return tagOptions || [];
    }
    const list = videoTags?.map((val: FilterItem) => val.t1) || [];
    return tagOptions?.filter((val) => list?.includes(val.label_en));
  }, [filter, tagOptions]);

  const getNavigatorOptions = useMemo(() => {

    if (navigators) {
      return [
        {
          label: formatMessage({ id: defaultNavigator }),
          id: defaultNavigator,
        },
        ...(navigators || []).map((item: any) => {
          const name = isEn ? item.label_en : item.label_zh;
          return {
            label: name,
            id: item.label_en,
          };
        }),
      ];
    }
    return [
      {
        label: formatMessage({ id: defaultNavigator }),
        id: defaultNavigator,
      }];
  }, [navigators, isEn]);

  return (
    <CardCommon
      className={"dbo-video-tag-analysis-card"}
      title={formatMessage('Tags Analysis')}
      subFilterBeforeElement={(
        <div className='dbo-video-tag-analysis-card-sub-filter'>
          <SortBySelect
            defaultValue={metric}
            onChange={(_key: string, value: any) => {
              setMetric(value);
              buriedBtnPoint('METRIC_SELECT', { metric: value }, value);
            }}
            defaultParams={{
              options: METRIC_OPTIONS,
              btnTextPrefix: formatMessage({ id: 'Metric' }),
              matchButtonWidth: true,
            }}
          />
          <RadioGroup
            value={attributeBy}
            onChange={(value) => {
              setAttributeBy(value);
              buriedBtnPoint('ATTRIBUTE_SELECT', { attribute: value }, value);
            }}
            className="tag-analysis switch-controls"
          >
            <span className="tag-analysis-label">
              {formatMessage('Attribute by')}
            </span>
            {
              AttributeOptions?.map(({ text, value }: AttributeOption, index: number) => (
                <Radio name={value} key={index}>
                  {formatMessage(text)}
                </Radio>
              ))
            }
          </RadioGroup>
        </div>
      )}
    >
      <>
        {navigators.length > 0 && (
          <TabNavigator
            scrollBody={scrollBody}
            options={getNavigatorOptions}
            onChange={(curNavigator: any) => {
              setSelectNavigator(curNavigator);
              scrollToAnchorPoint(curNavigator, 'start');
              buriedBtnPoint('NAVIGATOR_SELECT', { curNavigator }, curNavigator);
            }}
            value={selectNavigator}
            setValue={setSelectNavigator}
            scrollTopHeight={320}
          ></TabNavigator>
        )}
        <section
          className="dbo-video-tag-analysis-section"
          id={defaultNavigator}
        >
          {/* key={metric}: 强制组件重新挂载，重置 videoTagsTier 等内部状态 */}
          <TagAnalysisChart
            key={metric}
            filter={filter}
            title={defaultNavigator}
            attributeBy={attributeBy}
            metric={metric}
            tagOptions={tagOptions}
            defaultTier='t2'
            isSpecialEdition={isSpecialEdition(gameInfo)}
          />
        </section>
        {
          tagsList.map((val: { label_en: string; label_zh: string; }, index: number) => (
            <section
              key={index}
              className="dbo-video-tag-analysis-section"
              id={val.label_en}
            >
              {/* key={metric}: 强制组件重新挂载，重置 videoTagsTier 等内部状态 */}
              <TagAnalysisChart
                key={metric}
                filter={filter}
                title={val.label_en}
                attributeBy={attributeBy}
                metric={metric}
                tagOptions={tagOptions}
                titleCn={val.label_zh}
                isSpecialEdition={isSpecialEdition(gameInfo)}
                detailPage
              />
            </section>
          ))
        }
      </>
    </CardCommon>
  );
}